"use client";
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { submitQuickInquiry } from '@/lib/api'

const schema = z.object({
  customer_name: z.string().min(2, 'Name is required'),
  email: z.string().email('Valid email required'),
  company: z.string().optional(),
  country: z.string().optional(),
  message: z.string().min(5, 'Please describe your requirement'),
})

type FormData = z.infer<typeof schema>

interface QuickInquiryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  productName?: string
  productSku?: string
}

export function QuickInquiryDialog({ open, onOpenChange, productName, productSku }: QuickInquiryDialogProps) {
  const [submitting, setSubmitting] = useState(false)
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      message: productName
        ? `Hi, we are interested in ${productName}${productSku ? ` (SKU: ${productSku})` : ''}. Please share FOB pricing, MOQ, lead time, and available Incoterms for our destination.`
        : '',
    },
  })

  async function onSubmit(data: FormData) {
    setSubmitting(true)
    try {
      await submitQuickInquiry({
        customer_name: data.customer_name,
        email: data.email,
        company: data.company ?? '',
        country: data.country ?? '',
        product_sku: productSku ?? '',
        message: data.message,
      })
      toast.success('Inquiry sent', { description: 'Our export desk will respond within 1 business day.' })
      reset()
      onOpenChange(false)
    } catch {
      toast.error('Failed to send inquiry. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Export Inquiry</DialogTitle>
          <DialogDescription>
            {productName ? `Enquiring about: ${productName}` : 'Send us an export inquiry — we respond within 1 business day.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 mt-2">
          <div>
            <Label htmlFor="qi-name">Your Name *</Label>
            <Input id="qi-name" {...register('customer_name')} className="mt-1" placeholder="Full name" />
            {errors.customer_name && <p className="text-xs text-destructive mt-1">{errors.customer_name.message}</p>}
          </div>
          <div>
            <Label htmlFor="qi-email">Email *</Label>
            <Input id="qi-email" type="email" {...register('email')} className="mt-1" placeholder="you@company.com" />
            {errors.email && <p className="text-xs text-destructive mt-1">{errors.email.message}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="qi-company">Company</Label>
              <Input id="qi-company" {...register('company')} className="mt-1" placeholder="Optional" />
            </div>
            <div>
              <Label htmlFor="qi-country">Country</Label>
              <Input id="qi-country" {...register('country')} className="mt-1" placeholder="Destination" />
            </div>
          </div>
          <div>
            <Label htmlFor="qi-message">Message *</Label>
            <Textarea id="qi-message" {...register('message')} rows={4} className="mt-1" />
            {errors.message && <p className="text-xs text-destructive mt-1">{errors.message.message}</p>}
          </div>
          <Button type="submit" disabled={submitting} className="bg-primary text-primary-foreground hover:bg-primary/90">
            {submitting ? 'Sending...' : 'Send Inquiry'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
